/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { CustomLink } from "../../custom-link";
import get from "lodash/get";
import "./request-subscription.m.css";
import { SvgHandler } from "../../svg-handler/svg-handler";
import loaderIcon from "../../../../assets/icons/loading.svg";

export class PaywallBannerBase extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loader: false,
      successCallout: false,
      errorCallout: false
    };
  }

  openLogin = event => {
    event.preventDefault();
    event.stopPropagation();
    this.props.openLoginTemplate();
  };

  handleSubscriptionClick = async () => {
    if (!this.props.member) {
      this.props.openLoginTemplate();
      return false;
    }
    this.setState({ loader: true });
    const accessPlan = get(this.props, ["accessPlan"], {});
    const { story } = this.props;
    try {
      const { subscription } = await this.props.initRazorPayPayment(
        accessPlan,
        "dynamic_asset",
        story.id,
        story.headline,
        story.slug,
        story['["hero-image-s3-key"]']
      );
      if (subscription) {
        this.setState({ successCallout: true, loader: false });
        setTimeout(() => {
          this.setState({ successCallout: false, loader: false });
          this.props.navigateTo(`/${story.slug}`);
        }, 1000);
        return true;
      }
    } catch {
      this.setState({ errorCallout: true, loader: false, successCallout: false });
      console.log("user closed the payment");
    }
  };

  getLoginRequestBlock = () => {
    const { config } = this.props || {};
    const publisherTitle = get(config, ["publisher-settings", "title"], "Ahead");
    return (
      <>
        <div styleName="subs-headline">Login to get access</div>
        <p styleName="subs-desc">
          {`We're glad you're enjoying ${publisherTitle}. Login to continue reading this story.`}
        </p>
        <div styleName="btn-wrapper">
          <button styleName="primary-button" onClick={this.openLogin}>
            Login
          </button>
        </div>
      </>
    );
  };

  getSubscriptionRequestBlock = () => {
    const { config } = this.props || {};
    const PbAccessTypeConfig = get(config, ["pagebuilder-config", "general", "accesstypeIntegration"]);
    const enablePayPerAsset = get(
      PbAccessTypeConfig,
      ["enablePayPerAsset"],
      get(config, ["theme-attributes", "enablePayPerAsset"]) === "true"
    );

    const accessPrice = get(this.props, ["accessPlan", "price_cents"], "") / 100;
    const currencyType = get(this.props, ["accessPlan", "price_currency"]);
    const storyAccessLevel = get(this.props, ["story", "access"]);
    if (storyAccessLevel === "login") return this.getLoginRequestBlock();
    const magazineLinked = get(this.props, ["story", "linked-entities"], []).find(item => item.type === "magazine");
    const magazineSlug = get(magazineLinked, ["slug"], "");

    const paywallDescription = () => {
      if (enablePayPerAsset) {
        return (
          <>
            We’re glad you’re enjoying this story.{" "}
            <a styleName="link" className="paywall-ppa-button" onClick={e => this.handleSubscriptionClick(e)}>
              Buy this article for {currencyType} {accessPrice}{" "}
            </a>
            or subscribe to any of our plans to continue reading the story.
          </>
        );
      }
      return `We’re glad you’re enjoying this story. Subscribe to any of our plans to continue reading the story.`;
    };

    return (
      <>
        <div styleName="subs-headline">Want to read the full story?</div>
        <p styleName="subs-desc">{paywallDescription()}</p>
        <div styleName="btn-wrapper">
          {magazineSlug && (
            <div styleName="primary-button magazine-subscription">
              <CustomLink href={`/subscription?group=${magazineSlug}`}>Magazine Subscription</CustomLink>
            </div>
          )}
          <div styleName="primary-button">
            <CustomLink href={"/subscription"}>View All Plans</CustomLink>
          </div>
        </div>
        {!this.props.member && (
          <span styleName="info-text">
            Already have an account?{" "}
            <a styleName="link" onClick={this.openLogin}>
              Login
            </a>
          </span>
        )}
      </>
    );
  };

  handleTryAgain = () => this.setState({ errorCallout: false });

  render() {
    const { errorCallout, successCallout, loader } = this.state;
    if (loader) return <LoadingBlock />;
    return (
      <div className="element-wrapper">
        <div styleName="subs-wrapper">
          {errorCallout && <ErrorBlock onTryAgain={this.handleTryAgain} />}
          {successCallout && <ThankyouBlock />}
          {!errorCallout && !successCallout && this.getSubscriptionRequestBlock()}
        </div>
      </div>
    );
  }
}

const LoadingBlock = () => {
  return (
    <div styleName="block-wrapper processing-block">
      <div styleName="loader">
        <p>Payment is processing</p>
        <SvgHandler width="50" height="50" clazzName="callout-loader" xlinkHref={loaderIcon} />
      </div>
    </div>
  );
};

const ThankyouBlock = () => {
  return (
    <div styleName="block-wrapper">
      <div styleName="subs-headline success-msg-color">Thank you !</div>
      <p styleName="subs-desc">Your payment was successful. Now you can continue reading the article</p>
      <p>This notification will be closed in 1 seconds…</p>
    </div>
  );
};

const ErrorBlock = ({ onTryAgain }) => {
  return (
    <div className="element-wrapper">
      <div styleName="subs-wrapper">
        <div styleName="block-wrapper">
          <div styleName="subs-headline error-msg-color">Oops! Something went wrong.</div>
          <p styleName="subs-desc">Payment was not successful</p>
          <button styleName="try-again-btn" onClick={() => onTryAgain()}>
            Try again
          </button>
        </div>
      </div>
    </div>
  );
};
ErrorBlock.propTypes = {
  onTryAgain: PropTypes.fund
};

PaywallBannerBase.propTypes = {
  member: PropTypes.object,
  openLoginTemplate: PropTypes.func,
  initAccessType: PropTypes.func,
  initRazorPayPayment: PropTypes.func,
  getSubscriptionForUser: PropTypes.func,
  navigateTo: PropTypes.func,
  story: PropTypes.object
};

const mapStateToProps = state => ({
  member: get(state, ["member"], null),
  config: get(state, ["qt", "config"], {}),
  accessPlan: get(state, ["assetPlans", 0], {})
});

const mapDispatchToProps = dispatch => ({
  openLoginTemplate: () => dispatch({ type: "OPEN_LOGIN_TEMPLATE" }),
  navigateTo: url => global.app.navigateToPage(dispatch, url)
});

export const PaywallBanner = connect(
  mapStateToProps,
  mapDispatchToProps
)(PaywallBannerBase);

export default PaywallBanner;
